"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listPlaybooks, listAgentBackends, type PlaybookSummary, type AgentBackend } from "@/lib/api";

export type Selection =
  | { type: "playbook"; id: string }
  | { type: "agent_backend"; id: string };

type Mode = Selection["type"];

interface PlaybookSelectorProps {
  /** Called when user clicks Start. PlaybookSelector awaits the returned promise to show loading state. */
  onStart?: (selection: Selection) => Promise<void>;
  disabled: boolean;
  /** When false, hide the Start button and call onPlaybookChange instead. */
  autoStart?: boolean;
  onPlaybookChange?: (playbookId: string) => void;
  /** Hide the agent toggle and force playbook-only selection. */
  playbookOnly?: boolean;
}

export function PlaybookSelector({
  onStart,
  disabled,
  autoStart = true,
  onPlaybookChange,
  playbookOnly = false,
}: PlaybookSelectorProps) {
  const [mode, setMode] = useState<Mode>("playbook");
  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([]);
  const [agentBackends, setAgentBackends] = useState<AgentBackend[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    listPlaybooks().then((res) => setPlaybooks(res.playbooks));
  }, []);

  useEffect(() => {
    if (mode === "agent_backend" && agentBackends.length === 0) {
      listAgentBackends().then(setAgentBackends);
    }
  }, [mode, agentBackends.length]);

  function handleModeChange(newMode: Mode) {
    setMode(newMode);
    setSelected("");
    onPlaybookChange?.("");
  }

  function handleSelect(value: string) {
    setSelected(value);
    if (mode === "playbook") {
      onPlaybookChange?.(value);
    }
  }

  async function handleStart() {
    if (!selected || !onStart) return;
    setStarting(true);
    try {
      await onStart({ type: mode, id: selected });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      {!playbookOnly && (
        <div className="flex rounded-md border text-xs">
          <button
            type="button"
            className={`px-2 py-1 rounded-l-md transition-colors ${mode === "playbook" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            onClick={() => handleModeChange("playbook")}
            disabled={disabled}
          >
            话术
          </button>
          <button
            type="button"
            className={`px-2 py-1 rounded-r-md transition-colors ${mode === "agent_backend" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            onClick={() => handleModeChange("agent_backend")}
            disabled={disabled}
          >
            智能体
          </button>
        </div>
      )}
      <Select value={selected} onValueChange={handleSelect} disabled={disabled}>
        <SelectTrigger className="w-48">
          <SelectValue placeholder={mode === "playbook" ? "请选择话术" : "请选择智能体服务"} />
        </SelectTrigger>
        <SelectContent>
          {mode === "playbook"
            ? playbooks.map((pb) => (
                <SelectItem key={pb.id} value={pb.id}>{pb.name}</SelectItem>
              ))
            : agentBackends.map((ab) => (
                <SelectItem key={ab.id} value={ab.id}>{ab.display_name}</SelectItem>
              ))}
        </SelectContent>
      </Select>
      {autoStart && (
        <Button
          onClick={handleStart}
          disabled={!selected || disabled || starting}
        >
          {starting ? "启动中..." : "开始会话"}
        </Button>
      )}
    </div>
  );
}
